<template>
  <div id="UsageProfile">
    <h2 class="theme_name">
      <font-awesome-icon icon="laptop-house"/>
      {{ (!selectedId || selectedId === 8999) ? 'Region' : namesFromGeotype[geotype].singularName }} Usage
    </h2>
    <div style="padding: 2px; margin: 2px; font-size: small">
      <div>
        <UsageAboutPage class="no-print"/>
        Broadband usage (self-reported) shows information about households having and using high speed, cellular, or satellite internet
        service.
      </div>
    </div>

    <div id="charts" ref="charts">
      <div>
        <bigNum
          v-bind:format="formatPercent3sig"
          v-bind:color="'#64983D'"
          v-bind:num="totalInd.hhbroadband_21"
          v-bind:formatPercent="formatPercentOnBigNum"
          v-bind:numPercentNum="totalInd.hhbroadband_21 / totalInd.households_21*100"
        >
          households use broadband
        </bigNum>
        <bigNum
          v-bind:format="formatPercent3sig"
          v-bind:color="'#64983D'"
          v-bind:num="totalInd.hhpopage0_17_nobb_21"
          v-bind:formatPercent="formatPercentOnBigNum"
          v-bind:numPercentNum="totalInd.pcthhpopage0_17_nobb_21*100"
        >
          children without broadband
        </bigNum>
        <bigNum
          v-bind:format="formatPercent3sig"
          v-bind:color="'#64983D'"
          v-bind:num="totalInd.hhnointernet_21"
          v-bind:formatPercent="formatPercentOnBigNum"
          v-bind:numPercentNum="totalInd.hhnointernet_21 / totalInd.households_21*100"
        >
          households without internet
        </bigNum>
        <bigNum
          v-bind:format="formatPercent3sig"
          v-bind:color="'#64983D'"
          v-bind:num="totalInd.ehhpopage0_17_nocomputer_21"
          v-bind:formatPercent="formatPercentOnBigNum"
          v-bind:numPercentNum="totalInd.ehhpopage0_17_nocomputer_21 / totalInd.hhpopage0_17_21*100"
        >
          children without a desktop/laptop
        </bigNum>
        <bigNum
          v-bind:format="formatPercent3sig"
          v-bind:color="'#64983D'"
          v-bind:num="totalInd.hhnodesktoplaptop_21"
          v-bind:formatPercent="formatPercentOnBigNum"
          v-bind:numPercentNum="totalInd.pcthhnodesktoporlaptop_21*100"
        >
          households without a desktop/laptop
        </bigNum>
        <bigNum
          v-bind:format="formatPercent3sig"
          v-bind:color="'#64983D'"
          v-bind:num="totalInd.hhsmartphonetabletonly_21"
          v-bind:formatPercent="formatPercentOnBigNum"
          v-bind:numPercentNum="totalInd.hhsmartphonetabletonly_21 / totalInd.households_21*100"
        >
          households only use smart device
        </bigNum>
        <barChart v-bind:chartData="internetUseChart[0]"
                  v-bind:options="internetUseChart[1]"
                  v-bind:style="{width: '98%', height: '25vh',position: 'relative',  border: 'solid 1px lightgray'}"/>
        <barChart v-bind:chartData="hhShareByIncChart[0]"
                  v-bind:options="hhShareByIncChart[1]"
                  v-bind:style="{width: '98%', height: '25vh', position: 'relative',  border: 'solid 1px lightgray'}"/>
        <horizontalBar v-bind:chartData="BBUsageByAgeGroupChart[0]"
                  v-bind:options="BBUsageByAgeGroupChart[1]"
                  v-bind:style="{width: '98%', height: '25vh', position: 'relative', border: 'solid 1px lightgray'}"/>
        <horizontalBar v-bind:chartData="ageGroupWithoutCompChart[0]"
                  v-bind:options="ageGroupWithoutCompChart[1]"
                  v-bind:style="{width: '98%', height: '25vh', position: 'relative', border: 'solid 1px lightgray'}"/>
        <barChart v-bind:chartData="hhShareByRaceChart[0]"
                  v-bind:options="hhShareByRaceChart[1]"
                  v-bind:style="{width: '98%', height: '25vh', position: 'relative',  border: 'solid 1px lightgray'}"/>
        <barChart v-bind:chartData="hhShareNoCompByRaceChart[0]"
                  v-bind:options="hhShareNoCompByRaceChart[1]"
                  v-bind:style="{width: '98%', height: '25vh', position: 'relative',  border: 'solid 1px lightgray'}"/>
      </div>
    </div>
  </div>
</template>

<script>
import * as d3 from 'd3'
import barChart from "./charts/barChart.vue";
import horizontalBar from "./charts/horizontalBarChart.vue";
import bigNum from "./charts/bigNum.vue";
import UsageAboutPage from "./UsageAboutPage.vue";

export default {
  name: 'UsageProfile',
  components: {barChart, horizontalBar, bigNum, UsageAboutPage},
  props: [
    'selectedId',
    'selectedName',
    'allGeogData',
    'regionData',
    'namesFromGeotype',
    'geotype',
  ],
  methods: {
    shareURL: function () {
      let textarea = document.createElement('textarea');
      textarea.textContent = window.location.href;
      textarea.style.position = 'fixed';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy')
    },
    format: d3.format(',d'),
    formatOnLegend: d3.format('.2f'),
    formatPercentOnLegend: d3.format('.00%'),
    formatPercentOnBigNum: d3.format('.1f'),
    formatPercent2dec: d3.format('.2%'),
    formatPercent2sig: d3.format(".2s"),
    formatPercent3sig: d3.format(".3s"),
  },
  computed: {
    //Age Grp without a computer
    ageGroupWithoutCompChart: function () {
      return [{
        labels: ['0-17', '18-24', '25-64', '65+'],
        datasets: [
          { //#7F3C8D,#11A579,#3969AC,#F2B701,#E73F74,#80BA5A,#E68310,#008695,#CF1C90,#f97b72,#4b4b8f,#A5AA99
            backgroundColor: '#64983D',
            data: [this.totalInd.ehhpopage0_17_nocomputer_21/this.totalInd.hhpopage0_17_21,this.totalInd.ehhpopage18_24_nocomputer_21/this.totalInd.hhpopage18_24_21,this.totalInd.ehhpopage25_64_nocomputer_21/this.totalInd.hhpopage25_64_21,this.totalInd.hhpopage65andup_nocomputer_21/this.totalInd.ehhpopage65andover_21],
          },
        ],
      }, {
        title: {
          text: 'Lack of Computer by Age',
          fontSize: 18,
          fontColor: 'black',
          position: 'top',
          lineHeight: 2,
          padding: 20,
          display: true,
        },
        legend: {
          display: false,
        },
        tooltips: {
          callbacks: {
            label: (tooltipItem, data) => {
              var label = data.datasets[tooltipItem.datasetIndex].label || '';
              label += ': '
              label += this.formatPercentOnLegend(tooltipItem.xLabel);
              return label;
            },
          },
        },
        plugins: {
					datalabels: {
						color: 'black',
            textShadowBlur: 6,
            textShadowColor: 'white',
						display: true,
						font: {
							weight: 'bold',
						},
						formatter: this.formatPercentOnLegend,
					},
				},
        scales: {
          xAxes: [{
            display: false,
          }],
    },
        responsive: true,
        responsiveAnimationDuration: 1,
        maintainAspectRatio: false,
      }]
    },
    //BB Usage by Age Gro
    BBUsageByAgeGroupChart: function () {
      return [{
        labels: ['0-17', '18-24', '25-64', '65+'],
        datasets: [
          { //#5F4690,#1D6996,#38A6A5,#0F8554,#73AF48,#EDAD08,#E17C05,#CC503E,#94346E,#6F4070,#994E95,#666666
            label: 'Without Broadband',
            backgroundColor: '#64983D',
            data: [this.totalInd.pcthhpopage0_17_nobb_21,this.totalInd.pcthhpopage18_24_nobb_21,this.totalInd.pcthhpopage25_64_nobb_21,this.totalInd.pcthhpopage65andup_nobb_21],
          },
        ],
      }, {
        title: {
          text: 'Lack of Broadband By Age',
          fontSize: 18,
          fontColor: 'black',
          position: 'top',
          lineHeight: 2,
          padding: 20,
          display: true,
        },
        plugins: {
					datalabels: {
						color: 'black',
            textShadowBlur: 6,
            textShadowColor: 'white',
						display: true,
						font: {
							weight: 'bold',
						},
						formatter: this.formatPercentOnLegend,
					},
				},
        tooltips: {
          mode: 'index',
          intersect: false,
          callbacks: {
            label: (tooltipItem, data) => {
              var label = data.datasets[tooltipItem.datasetIndex].label || '';
              label += ': '
              label += this.formatPercentOnLegend(tooltipItem.xLabel);
              return label;
            },
          },
        },
        legend: {
          display: false,
        },
        scales: {
          xAxes: [{
            stacked: true,
            display: false,
          }],
          yAxes: [{
            stacked: true,
          }],
        },
        responsive: true,
        responsiveAnimationDuration: 1,
        maintainAspectRatio: false,
      }]
    },
    hhShareByIncChart: function () {
      return [{
        labels: ['<$20k', '$20k-$34k', '$35k-$49k', '$50k-$74k', '>$75k'],
        datasets: [
          {
            label: 'With Broadband',
            backgroundColor: ['#0a021f', '#51127C',
              '#e34f6f', '#faa476', '#fcde9c'],
            data: [this.totalInd.pcthhlessthan20000_bb_21, this.totalInd.pcthh_20000to34999_bb_21,
                this.totalInd.pcthh_35000to49999_bb_21, this.totalInd.pcthh_50000to74999_bb_21,
                this.totalInd.pcthh_75000ormore_bb_21],
          },
          { //#5F4690,#1D6996,#38A6A5,#0F8554,#73AF48,#EDAD08,#E17C05,#CC503E,#94346E,#6F4070,#994E95,#
            label: 'Without Broadband',
            backgroundColor: ['#cdcdd6', '#cdcdd6', '#cdcdd6', '#cdcdd6', '#cdcdd6', '#cdcdd6'],
            data: [this.totalInd.pcthhlessthan20000_nobb_21, this.totalInd.pcthh_20000to34999_nobb_21,
                this.totalInd.pcthh_35000to49999_nobb_21, this.totalInd.pcthh_50000to74999_nobb_21,
                this.totalInd.pcthh_75000ormore_nobb_21],
          },
        ],
      }, {
        title: {
          text: 'Broadband Share By Income',
          fontColor: 'black',
          fontSize: 18,
          position: 'top',
          lineHeight: 2,
          padding: 20,
          display: true,
        },
        plugins: {
					datalabels: {
						color: 'black',
            textShadowBlur: 6,
            textShadowColor: 'white',
						display: true,
						font: {
							weight: 'bold',
						},
						formatter: this.formatPercentOnLegend,
					},
				},
        legend: {
          display: false,
        },
        tooltips: {
          callbacks: {
            label: (tooltipItem, data) => {
              var label = data.datasets[tooltipItem.datasetIndex].label || '';
              label += ': '
              label += this.formatPercentOnLegend(tooltipItem.yLabel);
              return label;
            },
          },
        },
        scales: {
						xAxes: [{
							stacked: true,
						}],
						yAxes: [{
							stacked: true,
              display: false,
						}],
        },
        responsive: true,
        responsiveAnimationDuration: 1,
        maintainAspectRatio: false,
      }]
    },
    hhShareByRaceChart: function () {
      return [{
        labels: ['White', 'Black','Hispanic', 'Asian'],
        datasets: [
          {
            label: 'With Broadband',
            backgroundColor: ['#0F8554', '#EDAD08', '#CC503E', '#94346E'],
            data: [this.totalInd.hhpopnhwhite_bb_21/this.totalInd.hhpopnhwhite_21, this.totalInd.hhpopblk_bb_21/this.totalInd.hhpopblk_21,
                this.totalInd.hhpophisp_bb_21/this.totalInd.hhpophisp_21, this.totalInd.hhpopasian_bb_21/this.totalInd.hhpopasian_21],
          },
          { //#5F4690,#1D6996,#38A6A5,#0F8554,#73AF48,#EDAD08,#E17C05,#CC503E,#94346E,#6F4070,#994E95,#
            label: 'Without Broadband',
            backgroundColor: ['#cdcdd6', '#cdcdd6', '#cdcdd6', '#cdcdd6'],
            data: [1-(this.totalInd.hhpopnhwhite_bb_21/this.totalInd.hhpopnhwhite_21), 1-(this.totalInd.hhpopblk_bb_21/this.totalInd.hhpopblk_21),
                1-(this.totalInd.hhpophisp_bb_21/this.totalInd.hhpophisp_21), 1-(this.totalInd.hhpopasian_bb_21/this.totalInd.hhpopasian_21)],
          },
        ],
      }, {
        title: {
          text: 'Broadband Share By Race/Ethnicity',
          fontColor: 'black',
          fontSize: 18,
          position: 'top',
          lineHeight: 2,
          padding: 20,
          display: true,
        },
        plugins: {
					datalabels: {
						color: 'black',
            textShadowBlur: 6,
            textShadowColor: 'white',
						display: true,
						font: {
							weight: 'bold',
						},
						formatter: this.formatPercentOnLegend,
					},
				},
        legend: {
          display: false,
        },
        tooltips: {
          callbacks: {
            label: (tooltipItem, data) => {
              var label = data.datasets[tooltipItem.datasetIndex].label || '';
              label += ': '
              label += this.formatPercentOnLegend(tooltipItem.yLabel);
              return label;
            },
          },
        },
        scales: {
						xAxes: [{
							stacked: true,
						}],
						yAxes: [{
							stacked: true,
              display: false,
						}],
        },
        responsive: true,
        responsiveAnimationDuration: 1,
        maintainAspectRatio: false,
      }]
    },
    hhShareNoCompByRaceChart: function () {
      return [{
        labels: ['White', 'Black', 'Hispanic', 'Asian'],
        datasets: [
          {
            label: 'With Computer',
            backgroundColor: ['#0F8554', '#EDAD08', '#CC503E', '#94346E'],
            data: [1-(this.totalInd.hhpopnhwhite_nocomputer_21/this.totalInd.hhpopnhwhite_21), 1-(this.totalInd.hhpopblk_nocomputer_21/this.totalInd.hhpopblk_21),
                1-(this.totalInd.hhpophisp_nocomputer_21/this.totalInd.hhpophisp_21), 1-(this.totalInd.hhpopasian_nocomputer_21/this.totalInd.hhpopasian_21)],
          },
          { //#5F4690,#1D6996,#38A6A5,#0F8554,#73AF48,#EDAD08,#E17C05,#CC503E,#94346E,#6F4070,#994E95,#
            label: 'Without Computer',
            backgroundColor: ['#cdcdd6', '#cdcdd6', '#cdcdd6', '#cdcdd6'],
            data: [this.totalInd.hhpopnhwhite_nocomputer_21/this.totalInd.hhpopnhwhite_21, this.totalInd.hhpopblk_nocomputer_21/this.totalInd.hhpopblk_21,
                this.totalInd.hhpophisp_nocomputer_21/this.totalInd.hhpophisp_21, this.totalInd.hhpopasian_nocomputer_21/this.totalInd.hhpopasian_21],
          },
        ],
      }, {
        title: {
          text: 'Computer Share By Race/Ethnicity',
          fontColor: 'black',
          fontSize: 18,
          position: 'top',
          lineHeight: 2,
          padding: 20,
          display: true,
        },
        plugins: {
					datalabels: {
						color: 'black',
            textShadowBlur: 6,
            textShadowColor: 'white',
						display: true,
						font: {
							weight: 'bold',
						},
						formatter: this.formatPercentOnLegend,
					},
				},
        legend: {
          display: false,
        },
        tooltips: {
          callbacks: {
            label: (tooltipItem, data) => {
              var label = data.datasets[tooltipItem.datasetIndex].label || '';
              label += ': '
              label += this.formatPercentOnLegend(tooltipItem.yLabel);
              return label;
            },
          },
        },
        scales: {
						xAxes: [{
							stacked: true,
						}],
						yAxes: [{
							stacked: true,
              display: false,
						}],
        },
        responsive: true,
        responsiveAnimationDuration: 1,
        maintainAspectRatio: false,
      }]
    },
    internetUseChart: function () {
      return [{
        labels: ['Use Internet', 'No Internet', 'No Computer'],
        datasets: [
          {
            backgroundColor: ['#f8eb79', '#b31ddc', '#3c761b'],
            data: [this.totalInd.hhwithinternet_21, this.totalInd.hhnointernet_21, this.totalInd.households_21 - this.totalInd.hhwithanycomputer_21],
          },
        ],
      }, {
        title: {
          text: 'Households by Internet Usage',
          fontSize: 18,
          fontColor: 'black',
          position: 'top',
          display: true,
        },
        legend: {
          display: false,
        },
        tooltips: {
          callbacks: {
            label: (tooltipItem, data) => {
              var label = data.datasets[tooltipItem.datasetIndex].label || '';
              label += ': '
              label += this.format(tooltipItem.yLabel);
              return label;
            },
          },
        },
        plugins: {
					datalabels: {
						color: 'black',
            textShadowBlur: 6,
            textShadowColor: 'white',
						display: true,
						font: {
							weight: 'bold',
						},
						formatter: this.format,
					},
				},
        responsive: true,
        responsiveAnimationDuration: 1,
        maintainAspectRatio: false,
        scales: {
          yAxes: [{
            display: false,
            ticks: {
              beginAtZero: true,
            },
          }],
        },
      }]
    },
    totalInd: function () {
      if (!this.selectedId && !this.regionData) {
        return {}
      }
      let sel = (this.selectedId && this.allGeogData.find(geo => geo.geoid === this.selectedId))
      if (sel) {
        return sel
      }
      return this.regionData
    },
  },
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
  #UsageProfile {
    grid-column: 3;
    grid-row: 2;
    background: #fefefe;
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: max-content auto;
    min-height: 0;
  }

  #charts {
    overflow-y: auto;
    background: #fefefe;
    position: relative;
  }

  #charts > div {
    display: grid;
    grid-template-columns: 1fr 1fr;
    column-gap: 5px;
    row-gap: 5px;
    margin: 2px;
    padding: 7px;
    border: solid 2px #64983D;
    border-radius: 10px;
  }

    div#stats{
    grid-template-columns: 1fr;
  }

  .subgroup {
    margin-left: 15px;
  }

  h2, h3, h4, h5 {
    margin: 0;
  }

  div a {
    font-size: medium;
    color: #0f2d53;
    font-weight: bold;
    vertical-align: middle;
  }

  .controls {
    z-index: 3;
    padding: 4px;
    text-align: center;
  }

  .col_1_2 {
    grid-column-start: 1;
    grid-column-end: 1;
  }

  .col_3_wide {
    grid-column-start: 1;
    grid-column-end: 4;
  }

  .geoname {
    font-size: 1.9em;
    margin-left: 5px;
  }

    .theme_name {
    margin: 2px;
    font-size: 1.6em;
    font-weight: bolder;
    text-align: center;
    color: white;
    background-color: #64983D;
    border: solid 2px #64983D;
    border-radius: 10px;
  }

  .share {
    float: right;
    background: rgba(107, 153, 91, 0.7);
    border-radius: 4px;
    border-width: 1px;
    border-color: #9aa5a9;
  }
  .share:active{
      background: #9aa5a9;
  }

  .info {
    content: '';
    cursor: pointer;
    position: absolute;
    background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E %3Cpath fill='%23333333' fill-rule='evenodd' d='M4,10a6,6 0 1,0 12,0a6,6 0 1,0 -12,0 M9,7a1,1 0 1,0 2,0a1,1 0 1,0 -2,0 M9,10a1,1 0 1,1 2,0l0,3a1,1 0 1,1 -2,0'/%3E %3C/svg%3E");
    background-color: rgba(255, 255, 255, 0.5);
    width: 24px;
    height: 24px;
    box-sizing: border-box;
    border-radius: 12px;
  }

  /deep/ .my-tooltip {
    display: block !important;
    z-index: 1;
    font-size: small;
    max-width: 90%;
  }

  /deep/ .my-tooltip strong {
    font-size: medium;
  }

  /deep/ .my-tooltip small {
    color: #9aa5a9;
    font-size: small;
  }

  /deep/ .my-tooltip .my-tooltip-inner {
    background: #292424;
    color: white;
    opacity: 0.95;
    border-radius: 16px;
    padding: 5px 10px 4px;
  }

  /deep/ .my-tooltip .my-tooltip-arrow {
    width: 0;
    height: 0;
    border-style: solid;
    position: absolute;
    margin: 5px;
    border-color: #292424;
    opacity: 0.95;
    z-index: 1;
  }

  /deep/ .my-tooltip[x-placement^="top"] {
    margin-bottom: 10px;
  }

  /deep/ .my-tooltip[x-placement^="top"] .my-tooltip-arrow {
    border-width: 5px 5px 0 5px;
    border-left-color: transparent !important;
    border-right-color: transparent !important;
    border-bottom-color: transparent !important;
    bottom: -5px;
    left: calc(50% - 10px);
    margin-top: 0;
    margin-bottom: 0;
  }

  /deep/ .my-tooltip[x-placement^="bottom"] {
    margin-top: 10px;
  }

  /deep/ .my-tooltip[x-placement^="bottom"] .my-tooltip-arrow {
    border-width: 0 5px 5px 5px;
    border-left-color: transparent !important;
    border-right-color: transparent !important;
    border-top-color: transparent !important;
    top: -5px;
    left: calc(50% - 10px);
    margin-top: 0;
    margin-bottom: 0;
  }

  /deep/ .my-tooltip[x-placement^="right"] {
    margin-left: 10px;
  }

  /deep/ .my-tooltip[x-placement^="right"] .my-tooltip-arrow {
    border-width: 5px 5px 5px 0;
    border-left-color: transparent !important;
    border-top-color: transparent !important;
    border-bottom-color: transparent !important;
    left: -5px;
    top: calc(50% - 5px);
    margin-left: 0;
    margin-right: 0;
  }

  /deep/ .my-tooltip[x-placement^="left"] {
    margin-right: 10px;
  }

  /deep/ .my-tooltip[x-placement^="left"] .my-tooltip-arrow {
    border-width: 5px 0 5px 5px;
    border-top-color: transparent !important;
    border-right-color: transparent !important;
    border-bottom-color: transparent !important;
    right: -5px;
    top: calc(50% - 5px);
    margin-left: 0;
    margin-right: 0;
  }

  /deep/ .my-tooltip.popover .popover-inner {
    background: #f9f9f9;
    color: #292424;
    padding: 24px;
    border-radius: 5px;
    box-shadow: 0 5px 30px rgba(0, 0, 0, .1);
  }

  /deep/ .my-tooltip.popover .popover-arrow {
    border-color: #f9f9f9;
  }

  /deep/ .my-tooltip[aria-hidden='true'] {
    visibility: hidden;
    opacity: 0;
    transition: opacity .15s, visibility .15s;
  }

  /deep/ .my-tooltip[aria-hidden='false'] {
    visibility: visible;
    opacity: 1;
    transition: opacity .15s;
  }

  .single-ind {
    text-align: center;
  }

  .comm_dropdown {
    border-color: #f5f5f5;
    border-radius: 10px;
    max-width: 100%;
    font-size: large;
    margin-left: 3px;
  }

  .comm_dropdown:focus {
    outline: 0;
    border-color: black;
  }

  .comm_dropdown option {
    font-size: 0.8rem;
  }

  /deep/ .mapboxgl-ctrl-group > button {
    width: 29px;
    height: 29px;
  }


  /deep/ .legendColor {
    display: grid;
  }

  /deep/ .legendNames {
    display: grid;
  }

  @media (max-width: 1024px) {
    #UsageProfile {
      grid-column: 1 / -1;
      grid-row: 3;
    }
  }

  @media (max-width: 576px), print {
    #charts {
      overflow-y: unset;
    }

    #charts > div {
      grid-template-columns: 1fr;
    }

    #UsageProfile {
      grid-column: 1;
      grid-row: 4;
      overflow-y: unset;
    }

    .distribution {
      page-break-before: always;
    }
  }

  @media (max-width: 970px) {
    .comm_dropdown {
      width: 100%;
    }
  }
</style>
